import { createPortal } from 'react-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './ColorSelector.module.scss';
import clx from 'classnames';
import SelectorMenu from '../SelectorMenu';
import HoveredInfoIcon from '../../HoveredInfoIcon';
import { ReactComponent as ArrowRIcon } from '../../../../assets/svg/icons/arrow_r.svg';
import useOutsideClick from '../../../../utils/useOutsideClick';
import { useTranslation } from 'react-i18next';

const ColorSelector = ({ value, setValue, options, title, isFullWidth = false }) => {
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const { t } = useTranslation();

  const formedOptions = options.map((option) => ({
    ...option,
    background: option.value,
  }));

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, 150);
    const gap = 6;
    const estimatedHeight = Math.min((formedOptions.length || 1) * 45 + 8, 280);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - width - 8),
      width,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      zIndex: 10060,
    });
  };

  useLayoutEffect(() => {
    if (!isDropdownOpen) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isDropdownOpen, formedOptions.length]);

  useOutsideClick(dropdownRef, (event) => {
    if (!isDropdownOpen) return;
    if (menuRef.current?.contains(event.target)) return;
    setIsDropdownOpen(false);
  });

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDropdownOpen]);

  const menu =
    isDropdownOpen &&
    createPortal(
      <div
        ref={menuRef}
        className={styles.dropdown__menu}
        style={menuStyle}
        role="listbox"
      >
        <SelectorMenu
          isMultiple={false}
          setIsDropdownOpen={() => setIsDropdownOpen(false)}
          options={formedOptions}
          setValue={setValue}
          value={[value]}
        />
      </div>,
      document.body,
    );

  return (
    <div
      ref={dropdownRef}
      className={clx(styles.color_selector_wrapper, {
        [styles.color_selector_wrapper_open]: isDropdownOpen,
        [styles.color_selector_wrapper_full_width]: isFullWidth,
      })}
    >
      {title ? (
        <div className={styles.title_container}>
          <div className={styles.input_title}>{title}</div>
          <HoveredInfoIcon description={t('common.colorSelector.description')} />
        </div>
      ) : null}
      <div
        ref={triggerRef}
        onClick={() => setIsDropdownOpen((isOpen) => !isOpen)}
        className={styles.selector_container}
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
      >
        <div
          className={styles.value}
          style={{
            background: formedOptions.find((option) => option.value === value)?.background,
          }}
        />
        <ArrowRIcon
          style={{
            transform: isDropdownOpen ? 'rotate(90deg)' : 'rotate(-90deg)',
          }}
        />
      </div>
      {menu}
    </div>
  );
};

export default ColorSelector;
